import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { LegalService, type LegalStatus } from './LegalService';

/**
 * Drives the first-run legal-acceptance screen (GL-42): loads the current
 * document bundle and records the tenant admin's acceptance. The router owns the
 * gate itself (via `AuthContext.legal`); this VM only handles the screen.
 */
@injectable()
export class LegalAcceptanceViewModel extends BaseViewModel {
    status: LegalStatus | null = null;
    loading = true;
    submitting = false;
    error: string | null = null;

    constructor(private readonly legal: LegalService) {
        super();
    }

    async load(): Promise<void> {
        this.loading = true;
        this.error = null;
        this.notifyChange();
        try {
            this.status = await this.legal.getStatus();
        } catch (e) {
            this.error = e instanceof Error ? e.message : 'Failed to load terms';
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    /** Records acceptance; returns true on success so the page can leave the gate. */
    async accept(): Promise<boolean> {
        this.submitting = true;
        this.error = null;
        this.notifyChange();
        try {
            this.status = await this.legal.accept();
            return true;
        } catch (e) {
            this.error = e instanceof Error ? e.message : 'Failed to record acceptance';
            return false;
        } finally {
            this.submitting = false;
            this.notifyChange();
        }
    }
}
